import React, { useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { Grid, Stack } from '@mui/material';
import useImages from 'api/useImages';
import { EAssetType } from 'constants/enums';
import { useFormikContext } from 'formik';
import { UploadsTooltips } from 'hooks/useUpload';

import AcCard from 'components/AcCard/AcCard';
import AcInput from 'components/AcInput/AcInput';
import AcSelect from 'components/AcSelect/AcSelect';
import ActionButton from 'components/ActionButton/ActionButton';

import {
  CurrentTabItem,
  StoreThemeFormValues,
  TabsItem
} from '../storeTheme.types';

import { StoreTabsTable } from './StoreTabsTable';

export const StoreTabs: React.FC = () => {
  const { values, handleBlur, setFieldValue, errors } =
    useFormikContext<StoreThemeFormValues>();

  const { publisherId } = useParams();

  const pictures = useImages(publisherId).getImages;
  const [currentTab, setCurrentTab] = useState<CurrentTabItem | null>(null);
  const [tabs, setTabs] = useState<TabsItem[]>(values.tabs);
  const [editedTabId, setEditedTabId] = useState('');
  const [currentIdError, setCurrentIdError] = useState<string>('');
  const [currentNameError, setCurrentNameError] = useState<string>('');

  const tabsTableItems: TabsItem[] = useMemo(() => {
    if (tabs.length > 0) {
      return tabs.map((item, index) => ({
        ...item,
        _id: item.publisherTabId || index.toString(),
        type: 'Tab'
      }));
    }
    return [];
  }, [tabs]);

  useEffect(() => {
    if (values.tabs.length > 0) {
      setTabs(values.tabs);
    }
  }, [values.tabs]);

  const handleChangeTabsList = () => {
    return !!editedTabId ? editTab() : addTab();
  };

  const addTab = () => {
    if (!currentTab) return;

    const tabToAdd = {
      _id: currentTab.publisherTabId,
      publisherTabId: currentTab.publisherTabId,
      name: currentTab.name,
      iconUrl: currentTab.iconUrl,
      type: 'Tab'
    };
    setTabs((prevTabs) => [...prevTabs, tabToAdd]);

    const tabFieldValue: TabsItem = {
      _id: tabToAdd.publisherTabId || tabToAdd.name,
      publisherTabId: tabToAdd.publisherTabId,
      name: tabToAdd.name,
      iconUrl: tabToAdd.iconUrl,
      toAdd: true
    };
    setFieldValue('tabs', [...values.tabs, tabFieldValue]);
    setFieldValue('tabId', '');
    setFieldValue('tabName', '');
    setCurrentTab(null);
  };

  const editTab = () => {
    if (!currentTab) return;
    const updatedTabsList = tabs.map((tab) => {
      if (tab.publisherTabId === editedTabId) {
        const updatedTab: TabsItem = {
          _id: currentTab.publisherTabId,
          publisherTabId: currentTab.publisherTabId,
          tabId: currentTab.tabId,
          name: currentTab.name
        };
        if (currentTab.iconUrl) {
          updatedTab.iconUrl = currentTab.iconUrl;
        }
        return updatedTab;
      }
      return tab;
    });
    setEditedTabId('');
    setTabs(updatedTabsList);
    setFieldValue('tabs', updatedTabsList);
    setFieldValue('tabId', '');
    setFieldValue('tabName', '');
    setCurrentTab(null);
  };

  const getTabImgItems = () => {
    if (!pictures.isLoading && pictures.data) {
      const filteredPic = pictures.data.filter(
        (p: any) => p.type === EAssetType.GENERAL
      );
      return filteredPic.length > 0
        ? filteredPic.map((picture: any) => ({
            content: picture.name,
            key: picture.url,
            value: picture.url,
            renderFunction: () => (
              <Stack
                className="formContent-imageSelected"
                direction="row"
                spacing={2}
                overflow="hidden"
              >
                <img alt={picture.name} src={picture.url} />
                <span style={{ marginLeft: '8px' }}>{picture.name}</span>
              </Stack>
            )
          }))
        : [];
    }

    return [];
  };

  useEffect(() => {
    if (
      currentTab?.name &&
      values.tabs.some(
        (item: TabsItem) =>
          item.publisherTabId !== editedTabId && item.name === currentTab.name
      )
    ) {
      setCurrentNameError('Tab name already exists');
    } else {
      setCurrentNameError('');
    }
  }, [currentTab?.name]);

  useEffect(() => {
    if (
      currentTab?.publisherTabId &&
      currentTab.publisherTabId !== editedTabId &&
      values.tabs.some(
        (item: TabsItem) => item.publisherTabId === currentTab.publisherTabId
      )
    ) {
      setCurrentIdError('Tab ID already exists');
    } else if (
      currentTab?.publisherTabId &&
      currentTab.publisherTabId.includes(' ')
    ) {
      setCurrentIdError('Tab ID should not contain spaces');
    } else {
      setCurrentIdError('');
    }
  }, [currentTab?.publisherTabId]);

  return (
    <AcCard stackContainer={false} title="Tabs">
      <Grid container rowSpacing={1.5} columnSpacing={1.5}>
        <Grid item xs={6}>
          <AcInput
            header="Tab name"
            name="tabName"
            value={currentTab?.name || ''}
            tooltip="This will be the name of your tab"
            type="text"
            onChange={(e) => {
              setCurrentTab({
                ...currentTab,
                publisherTabId: currentTab?.publisherTabId || '',
                name: (e.target.value as string) || ''
              });
            }}
            onBlur={handleBlur}
            error={currentNameError !== ''}
            helperText={currentNameError}
          />
        </Grid>

        <Grid item xs={6}>
          <AcInput
            header="ID"
            name="tabId"
            value={currentTab?.publisherTabId || ''}
            type="text"
            onChange={(e) => {
              setCurrentTab({
                ...currentTab,
                publisherTabId: (e.target.value as string) || '',
                name: currentTab?.name || ''
              });
            }}
            onBlur={handleBlur}
            error={currentIdError !== ''}
            helperText={currentIdError}
          />
        </Grid>

        <Grid item xs={6}>
          <AcSelect
            header="Icon"
            name="tabIcon"
            value={currentTab?.iconUrl || ''}
            onChange={(e) => {
              setCurrentTab({
                ...currentTab,
                name: currentTab?.name || '',
                iconUrl: e.target.value as string,
                publisherTabId: currentTab?.publisherTabId || ''
              });
            }}
            onBlur={handleBlur}
            onClear={() => {
              if (currentTab) {
                const { iconUrl, ...rest } = currentTab;
                setCurrentTab(rest);
              }
            }}
            disabled={pictures.isLoading || pictures.isRefetching}
            uploadConfig={{
              onUploadSuccess: async (url: string) => {
                await pictures.refetch();
                setCurrentTab({
                  name: currentTab?.name || '',
                  iconUrl: url,
                  publisherTabId: currentTab?.publisherTabId || ''
                });
              },
              uploadType: EAssetType.GENERAL,
              uploadMessage: UploadsTooltips[EAssetType.GENERAL]
            }}
            items={getTabImgItems()}
            tooltip={
              'For optimized display, make sure your image are at least 300px with 1:1 aspect ratio'
            }
          />
        </Grid>

        <Grid item xs={12}>
          <ActionButton
            text={!!editedTabId ? 'Save' : 'Add'}
            variant="outlined"
            disabled={
              !currentTab?.publisherTabId ||
              !currentTab?.name ||
              Boolean(errors?.tabId) ||
              Boolean(errors?.tabName) ||
              currentIdError !== '' ||
              currentNameError !== ''
            }
            onClick={handleChangeTabsList}
          />
        </Grid>
        <Grid item xs={12} mt={-3}>
          <StoreTabsTable
            tabs={tabsTableItems}
            setTabs={setTabs}
            setFieldValue={setFieldValue}
            setCurrentTab={setCurrentTab}
            setEditedTabId={setEditedTabId}
          />
        </Grid>
      </Grid>
    </AcCard>
  );
};
